import { usePermissionsQuery } from "@/app/permissions/permissions.api";
import {
  useCreateRoleMutation,
  useRolePermissionAssignMutation,
  useRolePermissionRevokeMutation,
  useRolesQuery,
  useUpdateRoleMutation,
} from "@/app/roles/roles.api";
import { InputCheck } from "@/shared/components/atoms/InputCheck";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { RolesTable } from "@/shared/components/organisms/RolesTable";
import { Select } from "@/shared/components/organisms/Select";
import {
  roleInitialValues,
  roleValidationSchema,
} from "@/shared/schemas/role.schema";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Formik } from "formik";
import { t } from "i18next";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toasts } from "@/shared/utils/toasts";

export interface Permission {
  uuid: string;
  subgroup: string | null;
  group: string | null;
  description: string;
}

export interface Role {
  uuid?: string;
  name: string;
  permissions: Permission[];
  specialistType?: number;
  baseRole?: string;
}

const specialistOptions = [
  { id: 1, name: t("share.layouts.coach") },
  { id: 2, name: t("user.views.nutritionist") },
  { id: 3, name: t("user.views.physiotherapist") },
  { id: 4, name: t("user.views.psychologist") },
];

export const Permissions: FC<any> = () => {
  const { id } = useParams();
  const rolesQuery = useRolesQuery({ branchId: id });
  const permissionsQuery = usePermissionsQuery({ branchId: id });
  const [createRole, createRoleResult] = useCreateRoleMutation();
  const [updateRole, updateRoleResult] = useUpdateRoleMutation();
  const [rolePermissionAssign, rolePermissionAssignResult] =
    useRolePermissionAssignMutation();
  const [rolePermissionRevoke, rolePermissionRevokeResult] =
    useRolePermissionRevokeMutation();

  const roleOptions = useMemo(() => {
    return rolesQuery.data?.map((role: any) => ({
      id: role.name,
      name: role.name,
    }));
  }, [rolesQuery]);

  useEffect(() => {
    if (rolePermissionAssignResult.isSuccess) {
      toasts.success(
        "Permiso asignado",
        "Se ha asignado el permiso correctamente"
      );
    }

    if (rolePermissionAssignResult.isError) {
      toasts.danger(
        "Permiso no asignado",
        "Se ha producido un error al asignar el permiso"
      );
    }
  }, [rolePermissionAssignResult]);

  useEffect(() => {
    if (rolePermissionRevokeResult.isSuccess) {
      toasts.success(
        "Permiso revocado",
        "Se ha revocado el permiso correctamente"
      );
    }

    if (rolePermissionRevokeResult.isError) {
      toasts.danger(
        "Permiso no revocado",
        "Se ha producido un error al revocar el permiso"
      );
    }
  }, [rolePermissionRevokeResult]);

  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(!open);
  };

  const [currentRole, serCurrentRole] = useState<Role>();

  return (
    <Fragment>
      <div className="flex flex-col flex-1 h-full w-full overflow-y-scroll pt-8">
        <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8 px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center">
            <div className="flex-grow">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Roles y permisos
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                Asignar y eliminar permisos
              </p>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0"></div>
          </div>

          <div className="border-b border-gray-300 my-3"></div>

          <div className="flex items-center justify-end">
            <div className="sm: mb-5 md:mb-0">
              <button
                onClick={handleOpen}
                type="button"
                className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
              >
                <PlusIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col flex-1 mt-5">
            <div className="flex flex-col space-x-4 overflow-x-auto scrollbar-hide w-full">
              <RolesTable
                permissions={permissionsQuery.data ?? []}
                roles={rolesQuery.data ?? []}
                editRole={(role) => {
                  serCurrentRole({
                    ...role,
                    baseRole: roleOptions[0].id,
                    specialistType: specialistOptions[0].id,
                  });
                  handleOpen();
                }}
                onAssign={(role, permission, group, subgroup) => {
                  rolePermissionAssign({
                    branchId: id,
                    roleId: role.uuid,
                    permissionId: permission?.uuid,
                    group,
                    subgroup,
                  });
                }}
                onRevoke={(role, permission, group, subgroup) => {
                  rolePermissionRevoke({
                    branchId: id,
                    roleId: role.uuid,
                    permissionId: permission?.uuid,
                    group: group || null,
                    subgroup,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={currentRole ?? roleInitialValues}
        validationSchema={roleValidationSchema}
        onSubmit={async (values, formikHelper) => {
          try {
            if (currentRole) {
              await updateRole({
                ...values,
                branchId: id,
                id: currentRole.uuid,
              }).unwrap();
              toasts.success("Rol editado", "El rol ha sido editado con exito");
            } else {
              await createRole({
                ...values,
                branchId: id,
              }).unwrap();
              toasts.success("Rol creado", "El rol ha sido creado con exito");
            }

            formikHelper.resetForm();
            handleOpen();
            rolesQuery.refetch();
          } catch (error) {
            console.error("Error on handleSubmit", error);
          }
        }}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          errors,
          setFieldValue,
          resetForm,
        }) => (
          <DialogCreate
            open={open}
            setOpen={handleOpen}
            tittle={
              currentRole
                ? t("permissions.views.edit_rol")
                : t("permissions.views.create_rol")
            }
            tittleButtonCreate={
              currentRole ? t("general.edit") : t("general.create")
            }
            tittleButtonCancel={t("general.cancel")}
            OnclickCreate={handleSubmit}
            OnclickCancel={() => {
              handleOpen;
              serCurrentRole(null);
              resetForm();
            }}
            onBack={() => {
              handleOpen();
              serCurrentRole(null);
              resetForm();
            }}
            isLoading={createRoleResult.isLoading || updateRoleResult.isLoading}
            acceptDisabled={
              createRoleResult.isLoading || updateRoleResult.isLoading
            }
            onClose={() => serCurrentRole(null)}
          >
            <form action="">
              <div className="mt-6 mx-2">
                <div className="space-y-12">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {currentRole
                            ? t("permissions.views.edit_rol")
                            : t("permissions.views.create_rol")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("permissions.views.name_rol")}
                          placeholder={t("permissions.views.name_rol")}
                          span={t("general.mandatory")}
                          name="name"
                          value={values.name}
                          textError={errors.name}
                          error={!!errors.name}
                          onChange={handleChange}
                        />
                      </div>

                      {currentRole == null ? (
                        <div className="col-span-full">
                          <Select
                            name="baseRole"
                            tittle={t("permissions.views.parecido_rol")}
                            span={t("general.mandatory")}
                            error={!!errors.baseRole}
                            textError={errors.baseRole}
                            selected={roleOptions?.find(
                              (option) => option.id == values.baseRole
                            )}
                            setSelected={(option) =>
                              setFieldValue("baseRole", option.id)
                            }
                            options={roleOptions}
                          />
                        </div>
                      ) : null}

                      <div className="col-span-full">
                        <Select
                          name="specialistType"
                          tittle={t("assisted.views.especialist")}
                          span={t("general.optional")}
                          error={!!errors.specialistType}
                          textError={errors.specialistType}
                          selected={specialistOptions?.find(
                            (option) => option.id == values.specialistType
                          )}
                          setSelected={(option) =>
                            setFieldValue("specialistType", option.id)
                          }
                          options={specialistOptions}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </DialogCreate>
        )}
      </Formik>
    </Fragment>
  );
};
